/**
 * Fixed-timestep accumulator loop.
 *
 * The loop owns timing and nothing else: it decides how many fixed steps are
 * due and hands the leftover to the renderer as an interpolation factor. What
 * to interpolate between is the caller's business, because a host interpolates
 * two states of its own sim while a guest interpolates two snapshots that
 * arrived from someone else.
 *
 * Variable-delta stepping is never allowed.
 */
import { SIM_DT } from '@office/sim';

/** Clamp on a single frame's worth of catch-up: ~15 steps at 120 Hz. */
const MAX_FRAME_TIME = 0.125;

export interface LoopHooks {
  /** Advance the world exactly one fixed step. */
  step: () => void;
  /**
   * Draw. `alpha` is the fraction of a step left in the accumulator, `now` is
   * the frame timestamp in ms.
   */
  render: (alpha: number, now: number) => void;
  /** Called once per animation frame, before any steps. */
  frame?: (dtSeconds: number, now: number) => void;
}

export class FixedLoop {
  private accumulator = 0;
  private lastTime = 0;
  private raf = 0;
  private running = false;

  constructor(private readonly hooks: LoopHooks) {}

  start(): void {
    if (this.running) return;
    this.running = true;
    this.lastTime = performance.now();
    this.raf = requestAnimationFrame(this.tick);
  }

  stop(): void {
    this.running = false;
    cancelAnimationFrame(this.raf);
  }

  private readonly tick = (now: number): void => {
    if (!this.running) return;
    this.raf = requestAnimationFrame(this.tick);

    let frameTime = (now - this.lastTime) / 1000;
    this.lastTime = now;
    if (!Number.isFinite(frameTime) || frameTime < 0) frameTime = 0;
    if (frameTime > MAX_FRAME_TIME) frameTime = MAX_FRAME_TIME;

    this.hooks.frame?.(frameTime, now);
    this.accumulator += frameTime;

    while (this.accumulator >= SIM_DT) {
      this.accumulator -= SIM_DT;
      this.hooks.step();
    }

    this.hooks.render(this.accumulator / SIM_DT, now);
  };
}
